import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/config';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import TestBuilder from './TestBuilder';
import TestEditor from './TestEditor';
import './TestManager.css';

const TestManager = () => {
  const [tests, setTests] = useState([]);
  const [questionCounts, setQuestionCounts] = useState({});
  const [editingTestId, setEditingTestId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBuilder, setShowBuilder] = useState(false);

  const levels = [
    { id: 'level-1', name: 'Level 1 - Roots' },
    { id: 'level-2', name: 'Level 2 - Branches' },
    { id: 'level-3', name: 'Level 3 - Skies' },
    { id: 'level-4', name: 'Level 4 - Rivers' },
    { id: 'level-5', name: 'Level 5 - Mountains' }
  ];

  const testTypes = [
    { id: 'vocab', name: 'Vocabulary' },
    { id: 'grammar', name: 'Grammar' },
    { id: 'comprehension', name: 'Comprehension' }
  ];

  useEffect(() => {
    loadTests();
    loadQuestionCounts();
  }, []);

  const loadTests = async () => {
    try {
      setLoading(true);
      const querySnapshot = await getDocs(collection(db, 'tests'));
      const testsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTests(testsData);
    } catch (error) {
      console.error('Error loading tests:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadQuestionCounts = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'testQuestions'));
      const counts = {};
      
      querySnapshot.docs.forEach(doc => {
        const data = doc.data();
        const key = `${data.levelId}-${data.testType}`;
        counts[key] = (counts[key] || 0) + 1;
      });
      
      setQuestionCounts(counts);
    } catch (error) {
      console.error('Error loading question counts:', error);
    }
  };

  const initializeAllTests = async () => {
    if (!window.confirm('This will create all placement tests for levels 1-4 (4 levels × 3 types = 12 tests). Continue?')) {
      return;
    }

    try {
      const testsToCreate = [];

      // Only create for levels 1-4
      levels.slice(0, 4).forEach(level => {
        testTypes.forEach(testType => {
          testsToCreate.push({
            levelId: level.id,
            testType: testType.id,
            title: `${level.name} - ${testType.name}`,
            mode: 'placement',
            settings: {
              numberOfQuestions: 15,
              passThreshold: 80,
              showResults: true,
              showCorrectAnswers: true,
              scoringEnabled: true
            },
            performanceMessages: {
              low: `You have a good start. We recommend ${level.name} to build strong foundations.`,
              medium: `You can understand most of this. ${level.name} will help you feel confident.`,
              high: `Great! You're ready to use English independently from ${level.name}.`
            },
            createdAt: new Date().toISOString(),
            isActive: true
          });
        });
      });

      for (const testData of testsToCreate) {
        await addDoc(collection(db, 'tests'), testData);
      }

      alert('All placement tests created successfully!');
      loadTests();
    } catch (error) {
      console.error('Error creating tests:', error);
      alert('Failed to create tests. Check console for details.');
    }
  };

  const toggleTestStatus = async (testId, currentStatus) => {
    try {
      await updateDoc(doc(db, 'tests', testId), {
        isActive: !currentStatus
      });
      loadTests();
    } catch (error) {
      console.error('Error updating test status:', error);
      alert('Failed to update test status');
    }
  };

  const deleteTest = async (testId) => {
    if (!window.confirm('Are you sure you want to delete this test?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'tests', testId));
      alert('Test deleted successfully');
      loadTests();
    } catch (error) {
      console.error('Error deleting test:', error);
      alert('Failed to delete test');
    }
  };

  const handleTestCreated = () => {
    setShowBuilder(false);
    loadTests();
  };

  if (loading) {
    return <div className="loading">Loading tests...</div>;
  }

  // Show test builder modal
  if (showBuilder) {
    return (
      <div className="test-manager">
        <div className="builder-header">
          <button onClick={() => setShowBuilder(false)} className="back-btn">
            ← Back to Tests
          </button>
        </div>
        <TestBuilder onTestCreated={handleTestCreated} />
      </div>
    );
  }

  // Separate placement and diagnostic tests
  const placementTests = tests.filter(t => t.mode === 'placement' && !t.lessonCode && !t.oldLessonCode);
  const diagnosticTests = tests.filter(t => t.mode === 'diagnostic' || t.lessonCode || t.oldLessonCode);

  return (
    <div className="test-manager">
      <div className="manager-header">
        <h2>Test Management</h2>
        <div className="header-actions">
          <button onClick={() => setShowBuilder(true)} className="create-btn">
            ➕ Create Custom Test
          </button>
          {placementTests.length === 0 && (
            <button onClick={initializeAllTests} className="init-button">
              Initialize All Placement Tests
            </button>
          )}
        </div>
      </div>

      <div className="info-box">
        <h3>📋 Test Structure</h3>
        <ul>
          <li><strong>Placement Tests:</strong> 4 levels (Roots to Rivers) × 3 types = 12 tests</li>
          <li><strong>Diagnostic Tests:</strong> Created per lesson to assess if learner can skip</li>
          <li>Each test randomly selects questions from the question bank</li>
          <li>Pass threshold: 80%</li>
          <li>Results and correct answers shown after submission</li>
        </ul>
      </div>

      {/* Placement Tests Section */}
      <div className="tests-section">
        <h3 className="section-title">📊 Placement Tests</h3>
        
        {placementTests.length === 0 ? (
          <div className="empty-state">
            <p>No placement tests created yet.</p>
            <p>Click "Initialize All Placement Tests" to create all 12 tests at once.</p>
          </div>
        ) : (
          <div className="tests-grid">
            {levels.slice(0, 4).map(level => (
              <div key={level.id} className="level-section">
                <h3 className="level-title">{level.name}</h3>
                
                <div className="test-cards">
                  {testTypes.map(testType => {
                    const test = placementTests.find(
                      t => t.levelId === level.id && t.testType === testType.id
                    );
                    const questionKey = `${level.id}-${testType.id}`;
                    const questionCount = questionCounts[questionKey] || 0;

                    if (!test) {
                      return (
                        <div key={testType.id} className="test-card missing">
                          <div className="test-card-header">
                            <h4>{testType.name}</h4>
                            <span className="status-badge missing">Not Created</span>
                          </div>
                          <div className="test-card-body">
                            <p className="question-count">
                              {questionCount} questions available
                            </p>
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div key={test.id} className={`test-card ${test.isActive ? 'active' : 'inactive'}`}>
                        <div className="test-card-header">
                          <h4>{testType.name}</h4>
                          <span className={`status-badge ${test.isActive ? 'active' : 'inactive'}`}>
                            {test.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        
                        <div className="test-card-body">
                          <p className="question-count">
                            📝 {questionCount} questions in bank
                          </p>
                          <p className="test-info">
                            ✓ Selects {test.settings?.numberOfQuestions || 15} random questions
                          </p>
                          <p className="test-info">
                            🎯 Pass: {test.settings?.passThreshold || 80}%
                          </p>

                          {questionCount < (test.settings?.numberOfQuestions || 15) && (
                            <div className="warning">
                              ⚠️ Need at least {test.settings?.numberOfQuestions || 15} questions
                            </div>
                          )}
                        </div>

                        <div className="test-card-actions">
                          <button
                            onClick={() => toggleTestStatus(test.id, test.isActive)}
                            className={`toggle-btn ${test.isActive ? 'deactivate' : 'activate'}`}
                          >
                            {test.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                          <button
                            onClick={() => deleteTest(test.id)}
                            className="delete-btn"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Diagnostic Tests Section */}
      {diagnosticTests.length > 0 && (
        <div className="tests-section">
          <h3 className="section-title">📍 Diagnostic Tests</h3>
          
          <div className="diagnostic-tests-list">
            {diagnosticTests.map(test => {
              const level = levels.find(l => l.id === test.levelId);
              const testType = testTypes.find(t => t.id === test.testType);
              const questionKey = `${test.levelId}-${test.testType}`;
              const questionCount = questionCounts[questionKey] || 0;

              return (
                <div key={test.id} className={`diagnostic-test-card ${test.isActive ? 'active' : 'inactive'}`}>
                  <div className="diagnostic-header">
                    <div>
                      <h4>{test.title}</h4>
                      <p className="diagnostic-meta">
                        {level?.name} • {testType?.name} • Lesson {test.oldLessonCode || test.lessonCode}
                      </p>
                    </div>
                    <span className={`status-badge ${test.isActive ? 'active' : 'inactive'}`}>
                      {test.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>

                  <div className="diagnostic-body">
                    <div className="diagnostic-info">
                      <span>📝 {test.selectedQuestionIds?.length || questionCount} questions</span>
                      <span>🎯 Pass: {test.settings?.passThreshold || 80}%</span>
                      {test.newLessonUrl && <span>🔗 Redirects to: {test.newLessonUrl}</span>}
                    </div>
                  </div>

                  <div className="diagnostic-actions">
                    <button
                      onClick={() => setEditingTestId(test.id)}
                      className="edit-btn small"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => toggleTestStatus(test.id, test.isActive)}
                      className={`toggle-btn ${test.isActive ? 'deactivate' : 'activate'}`}
                    >
                      {test.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      onClick={() => deleteTest(test.id)}
                      className="delete-btn"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Test Editor Modal */}
      {editingTestId && (
        <TestEditor
          testId={editingTestId}
          onClose={() => setEditingTestId(null)}
          onTestUpdated={() => {
            loadTests();
            setEditingTestId(null);
          }}
        />
      )}
    </div>
  );
};

export default TestManager;